// A compact, forgiving JSON format for describing node trees. Designed so that an LLM
// can author a full screen in one call (flexbox-like shorthand, color strings, refs),
// and so the document can be read back with minimal tokens.

import type { Document } from './document.ts';
import type { Editor } from './editor.ts';
import type { Color, Effect, GridTrack, NodeData, NodeId, NodeProps, NodeType, Op, Paint, ParagraphFormat, ShaderSource, StrokeBrush } from './types.ts';
import { WIDTH_PROFILES, SCATTER_PRESETS } from './draw.ts';
import { shaderPreset, SHADER_PRESETS } from './shaders.ts';
import { makeNode, STICKY_COLORS } from './defaults.ts';
import { colorToHex, indicesBetween, newId, parseColor, isGradient } from './util.ts';
import { flattenPath, parseSvgPath, pathBounds, pathToSvg, transformPath } from './geometry.ts';
import { instantiate } from './components.ts';
import { findStyle, findVariable, findCollection, bindProblem } from './tokens.ts';
import { findVariant, componentSetOf } from './variants.ts';

export type ColorLike = string | Color;

export interface NodeSpec {
  type: string;
  /** Local reference name, usable by connectors (`from`/`to`) and returned in the id map. */
  ref?: string;
  name?: string;
  x?: number;
  y?: number;
  width?: number | 'hug' | 'fill';
  height?: number | 'hug' | 'fill';
  /** Same as width/height "hug" | "fill" | "fixed" (what get_nodes returns, so specs round-trip). */
  sizingH?: string;
  sizingV?: string;
  rotation?: number;
  opacity?: number;
  /** Blend mode: multiply, screen, overlay, darken, lighten, color-dodge, color-burn, hard-light, soft-light, difference, exclusion, hue, saturation, color, luminosity, linear-burn, linear-dodge, pass-through. */
  blend?: string;
  visible?: boolean;
  locked?: boolean;
  fill?: ColorLike | Paint | Paint[] | null;
  stroke?: ColorLike | Paint | Paint[] | null;
  /** One weight, or per side [top, right, bottom, left] (rectangles and frames). */
  strokeWidth?: number | [number, number, number, number];
  strokeAlign?: 'CENTER' | 'INSIDE' | 'OUTSIDE' | 'center' | 'inside' | 'outside';
  /** Ends of open strokes: none (flat, "butt"), round or square; Figma's NONE/ROUND/SQUARE too. */
  strokeCap?: string;
  /** Corners of strokes: miter, round or bevel; Figma's MITER/ROUND/BEVEL too. */
  strokeJoin?: string;
  dash?: number[];
  radius?: number | [number, number, number, number];
  /** Corner smoothing 0–1 (0.6 ≈ iOS squircle). */
  smoothing?: number;
  shadow?: boolean | { color?: ColorLike; x?: number; y?: number; blur?: number; spread?: number } | Effect[];
  /** Layer blur radius, or a progressive blur {radius, progressive: true, from?: start radius, start?: [x, y], end?: [x, y]} (0..1 in the layer). */
  blur?: number | { radius: number; progressive?: boolean; from?: number; start?: [number, number]; end?: [number, number] };
  /** Blur what's behind the layer (frosted glass), same forms as blur. */
  backgroundBlur?: number | { radius: number; progressive?: boolean; from?: number; start?: [number, number]; end?: [number, number] };
  innerShadow?: boolean | { color?: ColorLike; x?: number; y?: number; blur?: number; spread?: number };
  /** Glass: true, or {intensity 0..1, angle (deg), refraction 0..1, depth (px), dispersion 0..1, blur}. */
  glass?: boolean | { intensity?: number; angle?: number; refraction?: number; depth?: number; dispersion?: number; blur?: number };
  /** Grain (Figma Draw): true, or {type: mono|duo|multi, color, color2, size, density, opacity}. */
  noise?: boolean | { type?: string; color?: ColorLike; color2?: ColorLike; size?: number; density?: number; opacity?: number };
  /** Rough frosted edges (Figma Draw): true, or {size, radius, clip}. */
  texture?: boolean | { size?: number; radius?: number; clip?: boolean };
  /** Shader effect(s) on the layer's pixels: a built-in name (pixelate, duotone, chromatic-split) or {preset | source (Figma shader main.ts), params, time}. Shader fills go in "fill": [{"type": "shader", "preset": "aurora"}]. */
  shader?: SpecShader | SpecShader[];
  /** Variable-width stroke: a preset (uniform, wedge, taper, quarter-taper, eye, mirrored-taper) or width multipliers along the path [[0, 0.2], [0.5, 2], [1, 0.2]]. */
  widthProfile?: string | [number, number][] | { position: number; width: number }[];
  /** Dynamic stroke: {frequency (waves per 100px), wiggle (px)}. */
  dynamic?: { frequency?: number; wiggle: number; seed?: number };
  /** Brush: a preset name (calligraphy, marker, taper, ink, dots, stars, squares, confetti) or {type: stretch|scatter, preset?, source? (ref or id of a layer), angle, size, gap, sizeJitter, angleJitter, offsetJitter, rotateAlong}. */
  /** Make this layer a custom brush (Create brush) that strokes can use with brush: {source: <this ref>}. */
  brushDefinition?: 'stretch' | 'scatter';
  brush?: string | { type?: string; preset?: string; source?: string; angle?: number; size?: number; gap?: number; sizeJitter?: number; angleJitter?: number; offsetJitter?: number; rotateAlong?: boolean; seed?: number };
  clip?: boolean;
  // auto layout
  layout?: 'row' | 'column' | 'grid' | 'none' | 'HORIZONTAL' | 'VERTICAL' | 'GRID' | 'NONE';
  gap?: number;
  /** grid: a column count, or tracks like ["1fr", "240", "auto"] (fr = share of free space, auto = hug content) */
  columns?: number | (string | number)[];
  rows?: number | (string | number)[];
  rowGap?: number;
  /** children of a grid: 0-based cell and spans (unset = next free cell) */
  gridColumn?: number;
  gridRow?: number;
  colSpan?: number;
  rowSpan?: number;
  padding?: number | [number, number] | [number, number, number, number];
  align?: string; // primary axis: start | center | end | space-between
  crossAlign?: string; // counter axis: start | center | end | stretch
  wrap?: boolean;
  absolute?: boolean;
  // text: a string, or spans for mixed styles: [{"text": "Hello "}, {"text": "world", "fontWeight": 700, "color": "#f24822", "link": "https://…"}]
  text?: string | { text: string; fontWeight?: number | string; italic?: boolean; fontSize?: number; font?: string; color?: ColorLike; underline?: boolean; strike?: boolean; link?: string; letterSpacing?: number }[];
  font?: string;
  fontSize?: number;
  fontWeight?: number | 'thin' | 'light' | 'regular' | 'medium' | 'semibold' | 'bold' | 'black';
  italic?: boolean;
  color?: ColorLike; // text color
  textAlign?: 'left' | 'center' | 'right' | 'justified' | NodeData['textAlign'];
  verticalAlign?: 'top' | 'center' | 'bottom' | NodeData['verticalAlign'];
  lineHeight?: number;
  letterSpacing?: number;
  /** "upper" | "lower" | "title" | "original" */
  textCase?: string;
  /** Text that doesn't fit ends with "…": true (cut at the box), or a number of lines. */
  truncate?: boolean | number;
  /** "underline" | "strikethrough" | "none" */
  decoration?: string;
  /** Prototype: "vertical" | "horizontal" | "both" makes the frame scroll its overflowing content. */
  scroll?: string;
  /** Prototype: stays put while the parent scrolls. */
  fixed?: boolean;
  /** Dev Mode hand-off: "ready" | "completed" | null */
  devStatus?: string | null;
  /** Dev Mode notes: "Use the brand button" or [{"label": "…", "props": ["padding"]}] */
  annotations?: string | string[] | { label: string; props?: string[] }[];
  /** OpenType features: {"tnum": true, "smcp": true, "c2sc": true, "liga": false}. */
  features?: Record<string, boolean>;
  /** "bullet" | "number": every paragraph becomes a list item. */
  list?: string;
  /** Per-paragraph lists and nesting, e.g. [{"list": "bullet"}, {"list": "bullet", "level": 1}, {}]. */
  paragraphs?: { list?: string; level?: number }[];
  /** Read "- item", "1. item" and indented sub-items in `text` as real lists. */
  markdown?: boolean;
  /** Text on a path: SVG path data (local coords) the text follows, e.g. "M0 80 Q100 0 200 80". */
  onPath?: string;
  /** Distance along the path where the text starts (px). */
  pathOffset?: number;
  // shapes
  shape?: string;
  points?: number;
  innerRadius?: number;
  /** Ellipses: an arc, pie or donut — {start, end} in degrees clockwise from 3 o'clock, inner 0..1 (Figma's arcData). */
  arc?: { start?: number; end?: number; inner?: number };
  path?: string;
  // image
  src?: string;
  /** A video file (URL) the layer plays in prototypes; `src` is its poster frame. */
  video?: string;
  /** Video playback in prototypes (defaults all true). */
  playback?: { autoplay?: boolean; loop?: boolean; muted?: boolean };
  fit?: 'fill' | 'fit' | 'stretch';
  /** Image adjustments, each -1…1, e.g. {"exposure": 0.2, "saturation": -1}. */
  adjust?: { exposure?: number; contrast?: number; saturation?: number; temperature?: number };
  // connector
  from?: string | { x: number; y: number };
  to?: string | { x: number; y: number };
  line?: 'straight' | 'elbow' | 'curved';
  startCap?: string;
  endCap?: string;
  // component instance
  component?: string; // ref or node id of a COMPONENT (or of a component set)
  /** Instances of component sets: pick the variant, e.g. {"Size": "Large", "State": "Hover"}. */
  variant?: Record<string, string>;
  /** Instances: component property values, e.g. {"Label": "Buy now", "Show icon": false}. */
  properties?: Record<string, boolean | string>;
  /** Layers inside components: expose this layer's text / visibility as a component property. */
  exposeText?: string;
  exposeVisible?: string;
  /** Nested instances inside a component: expose which component they show (instance swap). */
  exposeSwap?: string;
  /** Nested instances inside a component: show their properties on instances of the component. */
  exposeNested?: boolean;
  /** Layout grids for frames, e.g. [{"pattern": "COLUMNS", "count": 12, "gutter": 24, "margin": 80}]. */
  grids?: NodeData['layoutGrids'];
  // design tokens (by name or id)
  fillStyle?: string;
  strokeStyle?: string;
  textStyle?: string;
  effectStyle?: string;
  /** Frames: a layout grid style. */
  gridStyle?: string;
  /** Bind properties to variables, e.g. {"fill": "colors/primary", "itemSpacing": "space/md"}. */
  bind?: Record<string, string>;
  /** Pick variable modes for this subtree, e.g. {"Theme": "Dark"}. */
  modes?: Record<string, string>;
  children?: NodeSpec[];
  /** Raw NodeData props, applied last (escape hatch). */
  props?: NodeProps;
  [key: string]: unknown;
}

const TYPE_ALIASES: Record<string, NodeType> = {
  frame: 'FRAME',
  component_set: 'FRAME',
  variants: 'FRAME',
  group: 'GROUP',
  section: 'SECTION',
  component: 'COMPONENT',
  instance: 'INSTANCE',
  rect: 'RECTANGLE',
  rectangle: 'RECTANGLE',
  ellipse: 'ELLIPSE',
  circle: 'ELLIPSE',
  line: 'LINE',
  polygon: 'POLYGON',
  triangle: 'POLYGON',
  star: 'STAR',
  vector: 'VECTOR',
  path: 'VECTOR',
  text: 'TEXT',
  image: 'IMAGE',
  sticky: 'STICKY',
  note: 'STICKY',
  shape: 'SHAPE_WITH_TEXT',
  shape_with_text: 'SHAPE_WITH_TEXT',
  connector: 'CONNECTOR',
  arrow: 'CONNECTOR',
};

const WEIGHTS: Record<string, number> = { thin: 100, extralight: 200, light: 300, regular: 400, normal: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800, black: 900 };
const weightOf = (w: number | string) => (typeof w === 'number' ? w : (WEIGHTS[w.toLowerCase().replace(/[\s_-]/g, '')] ?? 400));

const SPEC_KEYS = new Set(
  'type ref name x y width height sizingH sizingV rotation opacity blend visible locked fill stroke strokeWidth strokeAlign strokeCap strokeJoin dash radius smoothing shadow blur backgroundBlur innerShadow glass noise texture widthProfile dynamic brush brushDefinition shader clip layout gap columns rows rowGap gridColumn gridRow colSpan rowSpan padding align crossAlign wrap absolute text truncate font fontSize fontWeight italic color textAlign verticalAlign lineHeight letterSpacing textCase decoration scroll fixed devStatus annotations features list paragraphs markdown onPath pathOffset shape points innerRadius arc path src video playback fit adjust from to line startCap endCap component variant properties exposeText exposeVisible exposeSwap exposeNested grids fillStyle strokeStyle textStyle effectStyle gridStyle bind modes children props'.split(' '),
);

/** Figma Plugin API names agents reach for, mapped to the spec's own keys. */
const SPEC_ALIASES: Record<string, keyof NodeSpec> = {
  cornerRadius: 'radius',
  cornerSmoothing: 'smoothing',
  fills: 'fill',
  strokes: 'stroke',
  strokeWeight: 'strokeWidth',
  dashPattern: 'dash',
  effects: 'shadow',
  blendMode: 'blend',
  clipsContent: 'clip',
  layoutMode: 'layout',
  itemSpacing: 'gap',
  counterAxisSpacing: 'rowGap',
  primaryAxisAlignItems: 'align',
  counterAxisAlignItems: 'crossAlign',
  layoutSizingHorizontal: 'sizingH',
  layoutSizingVertical: 'sizingV',
  characters: 'text',
  fontFamily: 'font',
  textAlignHorizontal: 'textAlign',
  textAlignVertical: 'verticalAlign',
  textDecoration: 'decoration',
  imageUrl: 'src',
};
const PAD_SIDES = ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'] as const;
/** Stroke ends and corners by their spec, CSS and Figma names (any case). */
const STROKE_CAPS: Record<string, NonNullable<NodeData['strokeCap']>> = { none: 'NONE', butt: 'NONE', round: 'ROUND', square: 'SQUARE' };
const STROKE_JOINS: Record<string, NonNullable<NodeData['strokeJoin']>> = { miter: 'MITER', mitre: 'MITER', round: 'ROUND', bevel: 'BEVEL' };

/**
 * Accept Figma-style names (cornerRadius, fills, itemSpacing, paddingLeft, fontName, …) by
 * mapping them to spec keys, and report keys that mean nothing, so an agent never has a
 * property silently dropped. `where` names the spec in warnings (its ref, name or path).
 */
export function normalizeSpec(s: NodeSpec, warnings: string[], where = s.ref ?? s.name ?? s.type ?? 'node', partial = false): NodeSpec {
  if (!s || typeof s !== 'object') return s;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(s)) {
    const to = SPEC_ALIASES[k];
    if (to) {
      if (s[to] === undefined) out[to] = v;
      continue;
    }
    if (SPEC_KEYS.has(k) || (partial && k === 'id')) out[k] = v;
    else if (!(PAD_SIDES as readonly string[]).includes(k) && k !== 'fontName' && k !== 'layoutWrap') warnings.push(`${where}: unknown key "${k}" was ignored`);
  }
  // per-side padding
  if (s.padding === undefined && PAD_SIDES.some((k) => typeof s[k] === 'number')) out.padding = PAD_SIDES.map((k) => (s[k] as number) ?? 0);
  // fontName {family, style}: "Semi Bold Italic" → weight 600, italic
  const fn = s.fontName as { family?: string; style?: string } | undefined;
  if (fn && typeof fn === 'object') {
    if (fn.family && s.font === undefined && s.fontFamily === undefined) out.font = fn.family;
    const style = (fn.style ?? '').toLowerCase();
    if (style && s.fontWeight === undefined) out.fontWeight = weightOf(style.replace(/italic|oblique/g, '').trim() || 'regular');
    if (/italic|oblique/.test(style) && s.italic === undefined) out.italic = true;
  }
  if (s.layoutWrap !== undefined && s.wrap === undefined) out.wrap = s.layoutWrap === 'WRAP' || s.layoutWrap === true;
  for (const [k, names] of [
    ['strokeCap', STROKE_CAPS],
    ['strokeJoin', STROKE_JOINS],
  ] as const) {
    const v = out[k];
    if (v === undefined || (typeof v === 'string' && names[v.toLowerCase()])) continue;
    delete out[k];
    warnings.push(`${where}: ${k} "${String(v)}" was ignored (use ${Object.keys(names).join(', ')})`);
  }
  if (typeof out.type === 'string' && out.type.toLowerCase().replace(/[\s_-]/g, '') === 'mindmap') return { ...out, children: s.children } as NodeSpec; // topics have their own shape
  if (Array.isArray(s.children)) out.children = s.children.map((c, i) => normalizeSpec(c, warnings, `${where} › ${c?.ref ?? c?.name ?? `child ${i + 1}`}`));
  return out as NodeSpec;
}

export function resolveType(t: string): NodeType {
  const k = t.trim();
  const up = k.toUpperCase();
  const known: NodeType[] = ['FRAME', 'GROUP', 'SECTION', 'COMPONENT', 'INSTANCE', 'RECTANGLE', 'ELLIPSE', 'LINE', 'POLYGON', 'STAR', 'VECTOR', 'TEXT', 'IMAGE', 'STICKY', 'SHAPE_WITH_TEXT', 'CONNECTOR'];
  if ((known as string[]).includes(up)) return up as NodeType;
  const a = TYPE_ALIASES[k.toLowerCase()];
  if (!a) throw new Error(`Unknown node type "${t}". Use one of: ${Object.keys(TYPE_ALIASES).join(', ')}`);
  return a;
}

function toPaints(v: NodeSpec['fill'], type?: NodeType): Paint[] | undefined {
  if (v === undefined) return undefined;
  if (v === null || v === 'none') return [];
  if (Array.isArray(v)) return v.map((p) => normalizePaint(p));
  if (typeof v === 'string') {
    if (type === 'STICKY' && STICKY_COLORS[v.toLowerCase()]) return [{ type: 'SOLID', color: parseColor(STICKY_COLORS[v.toLowerCase()]) }];
    const g = v.match(/^linear-gradient\((.+)\)$/i);
    if (g) return [parseLinearGradient(g[1])];
    // "radial-gradient(#a, #b)", "conic-gradient(#a, #b)", "diamond-gradient(#a, #b)"
    const o = v.match(/^(radial|conic|angular|diamond)-gradient\((.+)\)$/i);
    if (o) {
      const lin = parseLinearGradient(o[2]) as Extract<Paint, { stops: unknown }>;
      const kind = o[1].toLowerCase();
      return [{ ...lin, type: kind === 'radial' ? 'GRADIENT_RADIAL' : kind === 'diamond' ? 'GRADIENT_DIAMOND' : 'GRADIENT_ANGULAR', from: { x: 0.5, y: 0.5 }, to: { x: 1, y: 0.5 } }];
    }
    return [{ type: 'SOLID', color: parseColor(v) }];
  }
  if ('type' in (v as object)) return [normalizePaint(v as Paint)];
  return [{ type: 'SOLID', color: v as Color }];
}

export type SpecShader = string | { preset?: string; source?: string; params?: Record<string, unknown>; name?: string; time?: number };

/** A shader from the spec: a preset name or {preset | source, params, name, time}. */
function shaderFromSpec(v: SpecShader, kind: 'fill' | 'effect'): ShaderSource {
  const o = typeof v === 'string' ? { preset: v } : v;
  let source = o.source;
  let name = o.name;
  if (!source) {
    const p = o.preset ? shaderPreset(o.preset) : null;
    if (!p) throw new Error(`shader: unknown preset "${o.preset ?? ''}" (built-ins: ${SHADER_PRESETS.join(', ')}; or pass "source": a Figma shader main.ts)`);
    if (p.kind !== kind) throw new Error(`shader: "${o.preset}" is a ${p.kind} — use it as ${p.kind === 'fill' ? '"fill": [{"type": "shader", "preset": "' + o.preset + '"}]' : '"shader": "' + o.preset + '"'}`);
    source = p.source;
    name ??= p.name;
  }
  return { source, ...(o.params ? { params: o.params } : {}), ...(name ? { name } : {}), ...(o.time !== undefined ? { time: o.time } : {}) };
}

function normalizePaint(p: Paint | string): Paint {
  if (typeof p === 'string') return toPaints(p)![0];
  if (String(p.type).toUpperCase() === 'SHADER') {
    const q = p as unknown as { preset?: string; source?: string; params?: Record<string, unknown>; name?: string; time?: number; opacity?: number; visible?: boolean };
    return { type: 'SHADER', ...shaderFromSpec(q, 'fill'), ...(q.opacity !== undefined ? { opacity: q.opacity } : {}), ...(q.visible === false ? { visible: false } : {}) };
  }
  // pattern fill: {type: "pattern", source: ref or id, scale?, spacing?: n | [x, y], tile?: rectangular | horizontal-hex | vertical-hex}
  if (String(p.type).toUpperCase() === 'PATTERN') {
    const q = p as unknown as { source?: string; sourceId?: string; scale?: number; spacing?: number | [number, number] | { x: number; y: number }; tile?: string; opacity?: number; visible?: boolean };
    const sp = q.spacing;
    const tile = String(q.tile ?? '').toLowerCase();
    return {
      type: 'PATTERN',
      sourceId: String(q.source ?? q.sourceId ?? ''),
      scale: q.scale,
      spacing: sp === undefined ? undefined : typeof sp === 'number' ? { x: sp, y: sp } : Array.isArray(sp) ? { x: sp[0], y: sp[1] } : sp,
      tile: tile.startsWith('h') ? 'HORIZONTAL_HEXAGONAL' : tile.startsWith('v') ? 'VERTICAL_HEXAGONAL' : undefined,
      opacity: q.opacity,
      visible: q.visible,
    };
  }
  if (p.type === 'SOLID' && typeof (p.color as unknown) === 'string') return { ...p, color: parseColor(p.color as unknown as string) };
  if (isGradient(p) && p.stops) {
    return { ...p, stops: p.stops.map((s) => ({ position: s.position, color: parseColor(s.color as unknown as string | Color) })) };
  }
  return p;
}

/** "to right, #f00, #00f" or "45deg, red 0%, blue 100%" */
function parseLinearGradient(args: string): Paint {
  const parts = args.split(/,(?![^(]*\))/).map((s) => s.trim());
  let angle = 180;
  if (/^(to |-?\d)/.test(parts[0])) {
    const a = parts.shift()!;
    const dirs: Record<string, number> = { 'to top': 0, 'to right': 90, 'to bottom': 180, 'to left': 270, 'to top right': 45, 'to bottom right': 135, 'to bottom left': 225, 'to top left': 315 };
    angle = dirs[a] ?? parseFloat(a);
  }
  const stops = parts.map((s, i) => {
    const m = s.match(/^(.*?)\s+(-?\d+(?:\.\d+)?)%$/);
    return { color: parseColor(m ? m[1] : s), position: m ? parseFloat(m[2]) / 100 : parts.length > 1 ? i / (parts.length - 1) : 0 };
  });
  const r = ((angle - 90) * Math.PI) / 180;
  const dx = Math.cos(r) / 2,
    dy = Math.sin(r) / 2;
  return { type: 'GRADIENT_LINEAR', stops, from: { x: 0.5 - dx, y: 0.5 - dy }, to: { x: 0.5 + dx, y: 0.5 + dy } };
}

function toEffects(s: NodeSpec): Effect[] | undefined {
  const out: Effect[] = [];
  let touched = false;
  if (s.shadow !== undefined) {
    touched = true;
    if (Array.isArray(s.shadow)) out.push(...s.shadow);
    else if (s.shadow) {
      const o = s.shadow === true ? {} : s.shadow;
      out.push({
        type: 'DROP_SHADOW',
        color: parseColor(o.color ?? '#00000026'),
        offset: { x: o.x ?? 0, y: o.y ?? 4 },
        radius: o.blur ?? 12,
        spread: o.spread ?? 0,
      });
    }
  }
  const blurOf = (type: 'LAYER_BLUR' | 'BACKGROUND_BLUR', v: NonNullable<NodeSpec['blur']>): Effect => {
    if (typeof v === 'number') return { type, radius: v };
    return { type, radius: v.radius, ...(v.progressive ? { blurType: 'PROGRESSIVE' as const, startRadius: v.from ?? 0, start: v.start ? { x: v.start[0], y: v.start[1] } : undefined, end: v.end ? { x: v.end[0], y: v.end[1] } : undefined } : {}) };
  };
  if (s.blur) {
    touched = true;
    out.push(blurOf('LAYER_BLUR', s.blur));
  }
  if (s.backgroundBlur) {
    touched = true;
    out.push(blurOf('BACKGROUND_BLUR', s.backgroundBlur));
  }
  if (s.innerShadow) {
    touched = true;
    const o = s.innerShadow === true ? {} : s.innerShadow;
    out.push({ type: 'INNER_SHADOW', color: parseColor(o.color ?? '#00000040'), offset: { x: o.x ?? 0, y: o.y ?? 2 }, radius: o.blur ?? 6, spread: o.spread ?? 0 });
  }
  if (s.noise) {
    touched = true;
    const o = s.noise === true ? {} : s.noise;
    const t = String(o.type ?? 'mono').toLowerCase();
    out.push({ type: 'NOISE', noiseType: t.startsWith('duo') ? 'DUOTONE' : t.startsWith('multi') ? 'MULTITONE' : 'MONOTONE', color: parseColor(o.color ?? '#000000'), secondaryColor: o.color2 ? parseColor(o.color2) : undefined, size: o.size ?? 1, density: o.density ?? 0.5, opacity: o.opacity ?? 0.25 });
  }
  if (s.glass) {
    touched = true;
    const o = s.glass === true ? {} : s.glass;
    out.push({ type: 'GLASS', lightIntensity: o.intensity ?? 0.5, lightAngle: o.angle ?? -45, refraction: o.refraction ?? 0.5, depth: o.depth ?? 6, dispersion: o.dispersion ?? 0.2, radius: o.blur ?? 4 });
  }
  if (s.texture) {
    touched = true;
    const o = s.texture === true ? {} : s.texture;
    out.push({ type: 'TEXTURE', size: o.size ?? 4, radius: o.radius ?? 4, clipToShape: o.clip ?? true });
  }
  if (s.shader) {
    touched = true;
    for (const v of Array.isArray(s.shader) ? s.shader : [s.shader]) out.push({ type: 'SHADER', ...shaderFromSpec(v, 'effect') });
  }
  return touched ? out : undefined;
}

const AXIS: Record<string, NodeData['primaryAxisAlign']> = { start: 'MIN', min: 'MIN', left: 'MIN', top: 'MIN', center: 'CENTER', middle: 'CENTER', end: 'MAX', max: 'MAX', right: 'MAX', bottom: 'MAX', 'space-between': 'SPACE_BETWEEN', between: 'SPACE_BETWEEN', space_between: 'SPACE_BETWEEN' };
const CROSS: Record<string, NodeData['counterAxisAlign']> = { start: 'MIN', min: 'MIN', left: 'MIN', top: 'MIN', center: 'CENTER', middle: 'CENTER', end: 'MAX', max: 'MAX', right: 'MAX', bottom: 'MAX', stretch: 'STRETCH' };

/** Convert spec shorthand into NodeData props (without children). */
export function specToProps(s: NodeSpec, type: NodeType): NodeProps {
  const p: NodeProps = {};
  const set = <K extends keyof NodeProps>(k: K, v: NodeProps[K] | undefined) => {
    if (v !== undefined) p[k] = v;
  };
  set('name', s.name);
  set('x', s.x);
  set('y', s.y);
  set('rotation', s.rotation);
  set('opacity', s.opacity);
  if (s.blend) set('blendMode', s.blend.toUpperCase().replace(/[\s-]+/g, '_') as NodeData['blendMode']);
  set('visible', s.visible);
  set('locked', s.locked);
  const textual = type === 'TEXT';
  if (typeof s.width === 'number') set('width', s.width);
  if (typeof s.height === 'number') set('height', s.height);
  if (s.width === 'hug') p.layoutSizingH = 'HUG';
  if (s.width === 'fill') p.layoutSizingH = 'FILL';
  if (s.height === 'hug') p.layoutSizingV = 'HUG';
  if (s.height === 'fill') p.layoutSizingV = 'FILL';
  const sz = (v?: string) => (v ? (({ hug: 'HUG', fill: 'FILL', fixed: 'FIXED' }) as Record<string, 'HUG' | 'FILL' | 'FIXED'>)[v.toLowerCase()] : undefined);
  if (sz(s.sizingH)) p.layoutSizingH = sz(s.sizingH);
  if (sz(s.sizingV)) p.layoutSizingV = sz(s.sizingV);
  if (textual) {
    if (typeof s.width === 'number') p.textAutoResize = typeof s.height === 'number' ? 'NONE' : 'HEIGHT';
    if (s.width === 'fill') p.textAutoResize = 'HEIGHT';
  }
  if (s.truncate !== undefined) {
    p.textTruncation = s.truncate === false ? 'DISABLED' : 'ENDING';
    p.maxLines = typeof s.truncate === 'number' && s.truncate > 0 ? Math.floor(s.truncate) : undefined;
  }
  if (type === 'TEXT') {
    if (s.fill !== undefined) set('fills', toPaints(s.fill));
    else if (s.color !== undefined) set('fills', toPaints(s.color));
  } else {
    set('fills', toPaints(s.fill, type));
    if (s.color !== undefined) set('textColor', parseColor(s.color));
  }
  set('strokes', toPaints(s.stroke));
  if (s.stroke && s.strokeWidth === undefined && type !== 'CONNECTOR' && type !== 'LINE' && type !== 'VECTOR' && type !== 'SHAPE_WITH_TEXT') set('strokeWeight', 1);
  if (Array.isArray(s.strokeWidth)) {
    set('strokeWeights', s.strokeWidth);
    set('strokeWeight', Math.max(...s.strokeWidth));
  } else set('strokeWeight', s.strokeWidth);
  set('strokeAlign', s.strokeAlign?.toUpperCase() as NodeData['strokeAlign'] | undefined);
  if (typeof s.strokeCap === 'string' && STROKE_CAPS[s.strokeCap.toLowerCase()]) p.strokeCap = STROKE_CAPS[s.strokeCap.toLowerCase()];
  if (typeof s.strokeJoin === 'string' && STROKE_JOINS[s.strokeJoin.toLowerCase()]) p.strokeJoin = STROKE_JOINS[s.strokeJoin.toLowerCase()];
  if (s.widthProfile !== undefined) {
    const wp = s.widthProfile;
    p.strokeWidthProfile = typeof wp === 'string' ? WIDTH_PROFILES[wp.toLowerCase().replace(/_/g, '-')] : wp.map((q) => (Array.isArray(q) ? { position: q[0], width: q[1] } : q));
  }
  if (s.brushDefinition) p.brushDefinition = s.brushDefinition.toUpperCase() === 'SCATTER' ? 'SCATTER' : 'STRETCH';
  if (s.dynamic) p.strokeDynamic = { frequency: s.dynamic.frequency ?? 5, wiggle: s.dynamic.wiggle, seed: s.dynamic.seed };
  if (s.brush) {
    const b = typeof s.brush === 'string' ? { preset: s.brush } : s.brush;
    const preset = b.preset?.toLowerCase();
    const type = b.type ? (b.type.toUpperCase() === 'SCATTER' ? 'SCATTER' : 'STRETCH') : preset && SCATTER_PRESETS.includes(preset) ? 'SCATTER' : 'STRETCH';
    p.strokeBrush = { type, preset, sourceId: b.source, angle: b.angle, size: b.size, gap: b.gap, sizeJitter: b.sizeJitter, angleJitter: b.angleJitter, offsetJitter: b.offsetJitter, rotateAlong: b.rotateAlong, seed: b.seed };
    for (const k of Object.keys(p.strokeBrush) as (keyof StrokeBrush)[]) if (p.strokeBrush[k] === undefined) delete p.strokeBrush[k];
    if (s.stroke === undefined && !p.strokes) p.strokes = [{ type: 'SOLID', color: { r: 0.12, g: 0.12, b: 0.12, a: 1 } }];
  }
  set('strokeDash', s.dash);
  set('cornerRadius', s.radius);
  set('cornerSmoothing', s.smoothing);
  set('effects', toEffects(s));
  if (s.clip !== undefined) set('clipsContent', s.clip);
  if (s.layout) {
    const l = s.layout.toLowerCase();
    p.layoutMode = l === 'row' || l === 'horizontal' ? 'HORIZONTAL' : l === 'column' || l === 'vertical' ? 'VERTICAL' : l === 'grid' ? 'GRID' : 'NONE';
    if (p.layoutMode !== 'NONE') {
      // as in Figma, an auto-layout frame's stroke counts in its padding
      p.strokesIncludedInLayout = true;
      if (p.layoutSizingH === undefined && s.width === undefined) p.layoutSizingH = 'HUG';
      if (p.layoutSizingV === undefined && s.height === undefined) p.layoutSizingV = 'HUG';
    }
  }
  set('itemSpacing', s.gap);
  if (s.columns !== undefined) p.gridColumns = parseTracks(s.columns, 'FLEX');
  if (s.rows !== undefined) p.gridRows = parseTracks(s.rows, 'HUG');
  if (s.rowGap !== undefined) p.counterAxisSpacing = s.rowGap;
  set('gridColumn', s.gridColumn);
  set('gridRow', s.gridRow);
  set('gridColumnSpan', s.colSpan);
  set('gridRowSpan', s.rowSpan);
  if (s.padding !== undefined) {
    const pad = typeof s.padding === 'number' ? [s.padding, s.padding, s.padding, s.padding] : s.padding.length === 2 ? [s.padding[0], s.padding[1], s.padding[0], s.padding[1]] : s.padding;
    [p.paddingTop, p.paddingRight, p.paddingBottom, p.paddingLeft] = pad as number[];
  }
  if (s.align) set('primaryAxisAlign', AXIS[s.align.toLowerCase()] ?? (s.align.toUpperCase() as NodeData['primaryAxisAlign']));
  if (s.crossAlign) set('counterAxisAlign', CROSS[s.crossAlign.toLowerCase()] ?? (s.crossAlign.toUpperCase() as NodeData['counterAxisAlign']));
  set('layoutWrap', s.wrap);
  if (s.absolute) p.layoutPositioning = 'ABSOLUTE';
  if (Array.isArray(s.text)) {
    let chars = '';
    const runs: NonNullable<NodeProps['textRuns']> = [];
    for (const span of s.text) {
      const start = chars.length;
      chars += span.text ?? '';
      const r: NonNullable<NodeProps['textRuns']>[number] = { start, end: chars.length };
      if (span.fontWeight !== undefined) r.fontWeight = weightOf(span.fontWeight);
      if (span.italic !== undefined) r.italic = span.italic;
      if (span.fontSize !== undefined) r.fontSize = span.fontSize;
      if (span.font) r.fontFamily = span.font;
      if (span.color) r.color = parseColor(span.color);
      if (span.underline) r.textDecoration = 'UNDERLINE';
      if (span.strike) r.textDecoration = 'STRIKETHROUGH';
      if (span.link) r.link = span.link;
      if (span.letterSpacing !== undefined) r.letterSpacing = span.letterSpacing;
      if (Object.keys(r).length > 2 && r.end > r.start) runs.push(r);
    }
    p.characters = chars;
    p.textRuns = runs.length ? runs : undefined;
  } else set('characters', s.text);
  set('fontFamily', s.font);
  set('fontSize', s.fontSize);
  if (s.fontWeight !== undefined) p.fontWeight = weightOf(s.fontWeight);
  set('italic', s.italic);
  if (s.textAlign) p.textAlign = s.textAlign.toUpperCase() as NodeData['textAlign'];
  if (s.verticalAlign) p.verticalAlign = s.verticalAlign.toUpperCase() as NodeData['verticalAlign'];
  set('lineHeight', s.lineHeight);
  if (s.textCase) p.textCase = ({ upper: 'UPPER', lower: 'LOWER', title: 'TITLE', original: 'ORIGINAL', none: 'ORIGINAL' } as Record<string, NodeData['textCase']>)[s.textCase.toLowerCase()] ?? 'ORIGINAL';
  if (s.decoration) p.textDecoration = /under/i.test(s.decoration) ? 'UNDERLINE' : /strike|through/i.test(s.decoration) ? 'STRIKETHROUGH' : 'NONE';
  const listKind = (v: string) => (/^(number|numbered|ordered|ol)$/i.test(v) ? 'NUMBER' : /^(none|plain)$/i.test(v) ? 'NONE' : 'BULLET');
  if (s.list) p.listStyle = listKind(s.list) === 'NUMBER' ? 'NUMBER' : 'BULLET';
  if (s.paragraphs) {
    const paras = s.paragraphs.map((x) => {
      const f: ParagraphFormat = {};
      if (x.list) f.list = listKind(x.list);
      if (x.level) f.level = x.level;
      return f;
    });
    p.paragraphs = paras.some((f) => f.list || f.level) ? paras : undefined;
  }
  if (s.onPath) {
    p.textPath = s.onPath;
    p.textAutoResize = 'NONE';
    // the layer's box is the path's bounds unless given
    const pts = flattenPath(parseSvgPath(s.onPath)).flatMap((l) => l.points);
    if (pts.length) {
      if (s.width === undefined) p.width = Math.max(1, Math.max(...pts.map((q) => q.x)));
      if (s.height === undefined) p.height = Math.max(1, Math.max(...pts.map((q) => q.y)));
    }
  }
  set('textPathOffset', s.pathOffset);
  if (s.markdown && typeof s.text === 'string') {
    const md = markdownLists(s.text);
    p.characters = md.characters;
    if (md.paragraphs.some((f) => f.list || f.level)) p.paragraphs = md.paragraphs;
  }
  set('letterSpacing', s.letterSpacing);
  if (s.scroll) {
    const v = s.scroll.toUpperCase();
    p.overflowScroll = v === 'VERTICAL' || v === 'HORIZONTAL' || v === 'BOTH' ? v : undefined;
    if (p.overflowScroll) p.clipsContent = true;
  }
  if (s.fixed !== undefined) p.scrollFixed = s.fixed || undefined;
  if (s.devStatus !== undefined) p.devStatus = s.devStatus ? (s.devStatus.toUpperCase() as 'READY') : undefined;
  if (s.annotations !== undefined) {
    const list = typeof s.annotations === 'string' ? [s.annotations] : s.annotations;
    p.annotations = list.map((a) => (typeof a === 'string' ? { label: a } : a)).filter((a) => a.label?.trim());
    if (!p.annotations.length) p.annotations = undefined;
  }
  if (s.features) p.openTypeFeatures = Object.fromEntries(Object.entries(s.features).map(([k, v]) => [k.toUpperCase(), v]));
  if (s.shape) p.shape = s.shape.toUpperCase().replace(/[- ]/g, '_') as NodeData['shape'];
  if (s.points !== undefined) p.pointCount = s.points;
  if (s.type.toLowerCase() === 'triangle') p.pointCount = 3;
  set('innerRadius', s.innerRadius);
  if (s.arc) p.arcData = { startingAngle: ((s.arc.start ?? 0) * Math.PI) / 180, endingAngle: ((s.arc.end ?? 360) * Math.PI) / 180, innerRadius: s.arc.inner ?? 0 };
  set('path', s.path);
  // a vector without a size gets the box of its path (so selection, hit testing and rasters
  // cover all of it), with the path moved to start at the box's corner
  if (type === 'VECTOR' && s.path && s.width === undefined && s.height === undefined) {
    const cmds = parseSvgPath(s.path);
    const b = pathBounds(cmds);
    if (cmds.length && Number.isFinite(b.width)) {
      p.path = b.x || b.y ? pathToSvg(transformPath(cmds, [1, 0, 0, 1, -b.x, -b.y])) : s.path;
      p.width = Math.max(0.01, b.width);
      p.height = Math.max(0.01, b.height);
      p.x = (s.x ?? 0) + b.x;
      p.y = (s.y ?? 0) + b.y;
    }
  }
  if (s.playback) p.videoPlayback = s.playback;
  if (s.video) {
    p.fills = [...(type === 'IMAGE' ? [] : (p.fills ?? [])), { type: 'IMAGE', src: s.src ?? '', video: s.video, scaleMode: (s.fit?.toUpperCase() as 'FILL') ?? 'FILL' }];
  } else if (type === 'IMAGE' && s.src) {
    p.fills = [{ type: 'IMAGE', src: s.src, scaleMode: (s.fit?.toUpperCase() as 'FILL' | 'FIT' | 'STRETCH') ?? 'FILL', ...(s.adjust ? { filters: s.adjust } : {}) }];
  } else if (s.src) {
    p.fills = [...(p.fills ?? []), { type: 'IMAGE', src: s.src, scaleMode: (s.fit?.toUpperCase() as 'FILL') ?? 'FILL', ...(s.adjust ? { filters: s.adjust } : {}) }];
  }
  if (s.line) p.connectorType = s.line.toUpperCase() as NodeData['connectorType'];
  if (s.startCap) p.startCap = s.startCap.toUpperCase() as NodeData['startCap'];
  if (s.endCap) p.endCap = s.endCap.toUpperCase() as NodeData['endCap'];
  if (s.grids) p.layoutGrids = s.grids;
  if (s.type.toLowerCase() === 'component_set' || s.type.toLowerCase() === 'variants') {
    Object.assign(p, { isComponentSet: true, strokes: p.strokes ?? [{ type: 'SOLID', color: parseColor('#9747ff') }], strokeDash: p.strokeDash ?? [6, 4], cornerRadius: p.cornerRadius ?? 6 });
    if (p.fills === undefined) p.fills = [];
    if (!p.layoutMode) Object.assign(p, { layoutMode: 'HORIZONTAL', itemSpacing: p.itemSpacing ?? 20, paddingTop: 20, paddingRight: 20, paddingBottom: 20, paddingLeft: 20, layoutSizingH: 'HUG', layoutSizingV: 'HUG' });
    p.clipsContent = false;
  }
  if (s.props) Object.assign(p, s.props);
  return p;
}

export interface BuildResult {
  /** ids of the top-level nodes created */
  ids: NodeId[];
  /** ref -> node id */
  refs: Record<string, NodeId>;
  ops: Op[];
  /** Keys that were not understood (and ignored), e.g. 'card: unknown key "colour" was ignored'. */
  warnings: string[];
}

interface BuildPlan {
  refs: Record<string, NodeId>;
  ops: Op[];
  /** Steps that need the first batch applied (instances of components created in the same batch). */
  deferred: (() => Op[])[];
  ids: NodeId[];
  /** Layers to expose as component properties once created. */
  exposes: { id: NodeId; field: 'visible' | 'characters' | 'componentId'; name: string }[];
}

/**
 * Build create ops for a list of specs under parentId. Connectors and instances may
 * reference refs anywhere in the batch, or existing node ids.
 */
export function planSpec(doc: Document, specs: NodeSpec[], parentId: NodeId, existingRefs: Record<string, NodeId> = {}, at?: number): BuildPlan {
  const refs: Record<string, NodeId> = { ...existingRefs };
  const ops: Op[] = [];
  const deferred: (() => Op[])[] = [];
  const exposes: BuildPlan['exposes'] = [];
  const created: NodeId[] = [];
  // refs as written ("card"), as batch writes them ("$card"), or ids ("12:3", "#12:3")
  const resolveRef = (r: string) => refs[r] ?? (r.startsWith('$') ? refs[r.slice(1)] : undefined) ?? (doc.has(r) ? r : r.startsWith('#') && doc.has(r.slice(1)) ? r.slice(1) : undefined);

  // First pass: assign ids to every ref so forward references work.
  const brushDefs = new Map<NodeId, string>();
  const assign = (list: NodeSpec[]) => {
    for (const s of list) {
      if (s.ref && !refs[s.ref]) refs[s.ref] = newId();
      if (s.ref && s.brushDefinition) brushDefs.set(refs[s.ref], s.brushDefinition.toUpperCase());
      if (s.children) assign(s.children);
    }
  };
  assign(specs);

  const build = (list: NodeSpec[], parent: NodeId, top: boolean) => {
    const parentNode = doc.get(parent);
    const siblings = parentNode ? doc.children(parent) : [];
    // top-level specs can go to a position among the parent's children (default: after the last)
    const pos = top && at !== undefined ? Math.max(0, Math.min(siblings.length, Math.floor(at))) : siblings.length;
    const idx = indicesBetween(siblings[pos - 1]?.index ?? null, siblings[pos]?.index ?? null, list.length);
    list.forEach((s, i) => {
      const type = resolveType(s.type);
      const id = s.ref ? refs[s.ref] : newId();
      const props = specToProps(s, type);
      Object.assign(props, tokenSpecProps(doc, s, type));
      // pattern fills and custom brushes name their source layer by ref or id
      const source = (r: string, what: string) => {
        const id = resolveRef(r);
        if (!id) throw new Error(`${what}: unknown layer "${r}" (use a ref from this batch or an existing node id)`);
        return id;
      };
      for (const list of [props.fills, props.strokes]) for (const pt of list ?? []) if (pt.type === 'PATTERN') pt.sourceId = source(pt.sourceId, 'pattern fill');
      if (props.strokeBrush?.sourceId) {
        const id = source(props.strokeBrush.sourceId, 'brush');
        // a brush made with Create brush knows whether it stretches or scatters
        const def = doc.get(id)?.brushDefinition ?? brushDefs.get(id);
        const typed = typeof s.brush === 'object' && !!s.brush.type;
        props.strokeBrush = { ...props.strokeBrush, sourceId: id, ...(!typed && def ? { type: def === 'SCATTER' ? 'SCATTER' : 'STRETCH' } : {}) };
      }
      // frames nested in a spec are layout wrappers: no fill unless one is given (top-level
      // frames keep the white canvas default)
      const nestedIn = parentNode && parentNode.type !== 'PAGE' && parentNode.type !== 'SECTION';
      if (type === 'FRAME' && (!top || nestedIn) && props.fills === undefined && !s.fillStyle && !s.bind?.fill) props.fills = [];
      if (type === 'INSTANCE' || s.component) {
        const compRef = s.component ?? '';
        // Instances use the ref's pre-assigned id so connectors can point at them.
        deferred.push(() => {
          let cid = resolveRef(compRef);
          if (!cid) throw new Error(`instance: unknown component "${compRef}"`);
          const target = doc.get(cid);
          if (target?.isComponentSet) cid = findVariant(doc, cid, s.variant ?? {})?.id ?? cid;
          else if (s.variant) {
            const set = componentSetOf(doc, cid);
            if (set) cid = findVariant(doc, set.id, s.variant)?.id ?? cid;
          }
          if (s.properties) {
            // instance-swap values may name a component by ref
            const defs = doc.get(cid)?.componentProps ?? {};
            const vals: Record<string, boolean | string> = {};
            for (const [k, v] of Object.entries(s.properties)) {
              let sv = v;
              if (defs[k]?.type === 'INSTANCE_SWAP' && typeof v === 'string') {
                const rid = resolveRef(v);
                if (!rid) throw new Error(`instance: unknown component "${v}" for property "${k}"`);
                const rn = doc.get(rid);
                sv = rn?.isComponentSet ? (doc.children(rid).find((c) => c.type === 'COMPONENT')?.id ?? rid) : rid;
              }
              vals[k] = sv;
            }
            props.propValues = vals;
          }
          if (s.exposeNested) props.exposedInstance = true;
          if (!s.exposeSwap && !s.exposeVisible) return instantiate(doc, cid, parent, idx[i], props, id).ops;
          // expose this nested instance as properties of its component right away,
          // so instances of that component created later in this spec already have them
          const comp = [doc.get(parent), ...doc.ancestors(parent)].find((n) => n?.type === 'COMPONENT');
          if (!comp) throw new Error(`"${s.exposeSwap ?? s.exposeVisible}": exposed layers must be inside a component`);
          const propRefs: NodeData['propRefs'] = {};
          const defs = { ...(comp.componentProps ?? {}) };
          if (s.exposeSwap) (propRefs.componentId = s.exposeSwap), (defs[s.exposeSwap] = { type: 'INSTANCE_SWAP', default: cid });
          if (s.exposeVisible) (propRefs.visible = s.exposeVisible), (defs[s.exposeVisible] = { type: 'BOOLEAN', default: props.visible ?? true });
          const r = instantiate(doc, cid, parent, idx[i], { ...props, propRefs }, id);
          return [...r.ops, { op: 'set', id: comp.id, props: { componentProps: defs } } as Op];
        });
        if (top) created.push(id);
        return;
      }
      if (type === 'CONNECTOR') {
        const connector = () => {
          const end = (v: NodeSpec['from']) => {
            if (v === undefined) return { x: 0, y: 0 };
            if (typeof v === 'string') {
              const nid = resolveRef(v);
              if (!nid) throw new Error(`connector: unknown node "${v}"`);
              return { nodeId: nid, magnet: 'AUTO' as const, x: 0, y: 0 };
            }
            return { x: v.x, y: v.y };
          };
          return [{ op: 'create', node: makeNode('CONNECTOR', { ...props, id, parent, index: idx[i], connectorStart: end(s.from), connectorEnd: end(s.to) }) } as Op];
        };
        deferred.push(connector);
        if (top) created.push(id);
        return;
      }
      if (type === 'TEXT' && props.characters === undefined) props.characters = '';
      if (s.exposeText) exposes.push({ id, field: 'characters', name: s.exposeText });
      if (s.exposeVisible) exposes.push({ id, field: 'visible', name: s.exposeVisible });
      const node = makeNode(type, { ...props, id, parent, index: idx[i] });
      ops.push({ op: 'create', node });
      if (top) created.push(id);
      if (s.children?.length) build(s.children, id, false);
    });
  };
  build(specs, parentId, true);
  return { refs, ops, deferred, ids: created, exposes };
}

/** Resolve token references (style / variable / mode names) in a spec. */
export function tokenSpecProps(doc: Document, s: NodeSpec, type: NodeType = resolveType(s.type)): NodeProps {
  const p: NodeProps = {};
  const style = (ref: string | undefined, key: 'fillStyleId' | 'strokeStyleId' | 'textStyleId' | 'effectStyleId' | 'gridStyleId', type: 'FILL' | 'TEXT' | 'EFFECT' | 'GRID') => {
    if (!ref) return;
    const st = findStyle(doc, ref, type);
    if (!st) throw new Error(`Unknown ${type.toLowerCase()} style "${ref}"`);
    p[key] = st.id;
  };
  style(s.fillStyle, 'fillStyleId', 'FILL');
  style(s.strokeStyle, 'strokeStyleId', 'FILL');
  style(s.textStyle, 'textStyleId', 'TEXT');
  style(s.effectStyle, 'effectStyleId', 'EFFECT');
  style(s.gridStyle, 'gridStyleId', 'GRID');
  if (s.bind) {
    const b: Record<string, NodeId> = {};
    for (const [prop, ref] of Object.entries(s.bind)) {
      const v = findVariable(doc, ref);
      if (!v) throw new Error(`Unknown variable "${ref}"`);
      const problem = bindProblem(v, { type }, prop);
      if (problem) throw new Error(problem);
      b[prop] = v.id;
    }
    p.boundVariables = b;
  }
  if (s.modes) {
    const m: Record<NodeId, string> = {};
    for (const [cref, mname] of Object.entries(s.modes)) {
      const c = findCollection(doc, cref);
      const mode = c?.modes?.find((x) => x.name.toLowerCase() === mname.toLowerCase() || x.id === mname);
      if (!c || !mode) throw new Error(`Unknown mode "${cref}: ${mname}"`);
      m[c.id] = mode.id;
    }
    p.variableModes = m;
  }
  return p;
}

/** Create nodes from specs through the editor (one undo step). */
/** A mind map in a spec: `{type: 'mindmap', text, layout?: 'right'|'both', children: [topic | "text"]}`. */
type MindSpec = { text?: string; ref?: string; children?: (MindSpec | string)[] };

function buildMindMap(editor: Editor, s: NodeSpec, parent: NodeId, refs: Record<string, NodeId>): NodeId {
  const layout = String((s as { layout?: string }).layout ?? 'right').toUpperCase() === 'BOTH' ? 'BOTH' : 'RIGHT';
  const text = typeof s.text === 'string' ? s.text : s.text?.map((r) => r.text).join('');
  const root = editor.createMindMap(s.x ?? 0, s.y ?? 0, text ?? s.name ?? 'Central topic', { parent, layout });
  if (s.ref) refs[s.ref] = root;
  const grow = (topic: NodeId, kids: (MindSpec | string)[] | undefined) => {
    for (const k of kids ?? []) {
      const t = typeof k === 'string' ? { text: k } : k;
      const id = editor.addMindTopic(topic, t.text ?? '');
      if (t.ref) refs[t.ref] = id;
      grow(id, t.children);
    }
  };
  grow(root, s.children as (MindSpec | string)[] | undefined);
  return root;
}

export function createFromSpec(editor: Editor, specs: NodeSpec | NodeSpec[], parentId?: NodeId, refs: Record<string, NodeId> = {}, opts: { index?: number } = {}): BuildResult {
  const warnings: string[] = [];
  const all0 = (Array.isArray(specs) ? specs : [specs]).map((s) => normalizeSpec(s, warnings));
  const isMind = (s: NodeSpec) => s.type.toLowerCase().replace(/[\s_-]/g, '') === 'mindmap';
  const list = all0.filter((s) => !isMind(s));
  const parent = parentId ?? editor.pageId;
  const all: Op[] = [];
  let plan!: BuildPlan;
  editor.transact('Create from spec', () => {
    const minds = all0.filter(isMind).map((s) => buildMindMap(editor, s, parent, refs));
    plan = planSpec(editor.doc, list, parent, refs, opts.index);
    plan.ids.push(...minds);
    editor.applyOps(plan.ops);
    all.push(...plan.ops);
    // expose component properties before instances (in deferred steps) are created
    for (const x of plan.exposes) editor.addComponentProperty(x.id, x.field, x.name);
    for (const step of plan.deferred) {
      const ops = step();
      editor.applyOps(ops);
      all.push(...ops);
    }
  });
  return { ids: plan.ids, refs: plan.refs, ops: all, warnings };
}

function parseTracks(v: number | (string | number)[], countType: 'FLEX' | 'HUG'): GridTrack[] {
  if (typeof v === 'number') return Array.from({ length: Math.max(1, Math.round(v)) }, () => (countType === 'FLEX' ? { type: 'FLEX', value: 1 } : { type: 'HUG' }));
  return v.map((t): GridTrack => {
    if (typeof t === 'number') return { type: 'FIXED', value: t };
    const s = t.trim().toLowerCase();
    if (s === 'auto' || s === 'hug') return { type: 'HUG' };
    const fr = s.match(/^([\d.]+)\s*fr$/);
    if (fr) return { type: 'FLEX', value: Number(fr[1]) };
    return { type: 'FIXED', value: parseFloat(s) || 100 };
  });
}

function tracksToSpec(t: GridTrack[]): (string | number)[] {
  return t.map((x) => (x.type === 'FIXED' ? (x.value ?? 100) : x.type === 'HUG' ? 'auto' : `${x.value ?? 1}fr`));
}

/** "- a\n  - b\n1. c" → plain paragraphs plus list formats. */
export function markdownLists(text: string): { characters: string; paragraphs: ParagraphFormat[] } {
  const lines: string[] = [];
  const paragraphs: ParagraphFormat[] = [];
  for (const raw of text.split('\n')) {
    const m = raw.match(/^(\s*)([-*•+]|\d+[.)])\s+(.*)$/);
    if (!m) {
      lines.push(raw);
      paragraphs.push({});
      continue;
    }
    const level = Math.floor(m[1].replace(/\t/g, '  ').length / 2);
    lines.push(m[3]);
    paragraphs.push({ list: /\d/.test(m[2]) ? 'NUMBER' : 'BULLET', ...(level ? { level } : {}) });
  }
  return { characters: lines.join('\n'), paragraphs };
}

// ---------------------------------------------------------------------------
// Reading the document back compactly
// ---------------------------------------------------------------------------

function paintToSpec(p: Paint): unknown {
  if (p.type === 'SOLID' && (p.opacity ?? 1) === 1 && p.visible !== false) return colorToHex(p.color);
  if (p.type === 'SOLID') return { type: 'SOLID', color: colorToHex(p.color), opacity: p.opacity, visible: p.visible };
  if (p.type === 'IMAGE') return { type: 'IMAGE', src: p.src.length > 120 ? p.src.slice(0, 60) + '…' : p.src, ...(p.video ? { video: p.video.length > 120 ? p.video.slice(0, 60) + '…' : p.video } : {}), scaleMode: p.scaleMode, filters: p.filters };
  if (p.type === 'SHADER') return { type: 'shader', ...(p.name ? { name: p.name } : {}), source: p.source, ...(p.params ? { params: p.params } : {}), ...(p.time ? { time: p.time } : {}), ...(p.opacity !== undefined ? { opacity: p.opacity } : {}) };
  if (p.type === 'PATTERN') return { type: 'PATTERN', source: p.sourceId, scale: p.scale, spacing: p.spacing, tile: p.tile, opacity: p.opacity, visible: p.visible };
  return { ...p, stops: p.stops.map((s) => ({ position: s.position, color: colorToHex(s.color) })) };
}

function paintsToSpec(ps: Paint[]): unknown {
  if (!ps.length) return undefined;
  if (ps.length === 1) return paintToSpec(ps[0]);
  return ps.map(paintToSpec);
}

const r2 = (v: number) => Math.round(v * 100) / 100;

/** Compact spec-like JSON for a node (and optionally its descendants), omitting defaults. */
export function nodeToSpec(doc: Document, id: NodeId, depth = Infinity): Record<string, unknown> {
  const n = doc.must(id);
  const def = makeNode(n.type, { id: 'x' });
  const out: Record<string, unknown> = { id: n.id, type: n.type, name: n.name };
  if (n.type !== 'PAGE' && n.type !== 'DOCUMENT') {
    out.x = r2(n.x);
    out.y = r2(n.y);
    out.width = r2(n.width);
    out.height = r2(n.height);
  }
  if (n.rotation) out.rotation = r2(n.rotation);
  if (!n.visible) out.visible = false;
  if (n.locked) out.locked = true;
  if (n.videoPlayback) out.playback = n.videoPlayback;
  if (n.opacity !== 1) out.opacity = r2(n.opacity);
  if (n.blendMode && n.blendMode !== 'NORMAL' && n.blendMode !== 'PASS_THROUGH') out.blend = n.blendMode.toLowerCase().replace(/_/g, '-');
  const textual = n.type === 'TEXT';
  if (JSON.stringify(n.fills) !== JSON.stringify(def.fills) || textual) {
    const f = paintsToSpec(n.fills);
    if (textual) out.color = f;
    else out.fill = f ?? null;
  }
  if (n.arcData) out.arc = { start: +((n.arcData.startingAngle * 180) / Math.PI).toFixed(2), end: +((n.arcData.endingAngle * 180) / Math.PI).toFixed(2), inner: n.arcData.innerRadius };
  if (n.brushDefinition) out.brushDefinition = n.brushDefinition.toLowerCase();
  if (n.strokes.length) {
    out.stroke = paintsToSpec(n.strokes);
    out.strokeWidth = n.strokeWeights ?? n.strokeWeight;
    if (n.strokeAlign !== def.strokeAlign) out.strokeAlign = n.strokeAlign.toLowerCase();
    if (n.strokeCap && n.strokeCap !== 'NONE') out.strokeCap = n.strokeCap.toLowerCase();
    if (n.strokeJoin && n.strokeJoin !== 'MITER') out.strokeJoin = n.strokeJoin.toLowerCase();
    if (n.strokeDash?.length) out.dash = n.strokeDash;
    if (n.strokeWidthProfile?.length) out.widthProfile = n.strokeWidthProfile.map((q) => [q.position, q.width]);
    if (n.strokeDynamic) out.dynamic = n.strokeDynamic;
    if (n.strokeBrush) out.brush = { type: n.strokeBrush.type.toLowerCase(), ...(n.strokeBrush.sourceId ? { source: n.strokeBrush.sourceId } : {}), ...Object.fromEntries(Object.entries(n.strokeBrush).filter(([k]) => k !== 'type' && k !== 'sourceId')) };
  }
  if (JSON.stringify(n.cornerRadius) !== JSON.stringify(def.cornerRadius)) out.radius = n.cornerRadius;
  if (n.cornerSmoothing) out.smoothing = n.cornerSmoothing;
  if (n.effects.length && JSON.stringify(n.effects) !== JSON.stringify(def.effects)) out.effects = n.effects;
  if (n.layoutMode && n.layoutMode !== 'NONE') {
    out.layout = n.layoutMode === 'HORIZONTAL' ? 'row' : n.layoutMode === 'GRID' ? 'grid' : 'column';
    if (n.itemSpacing) out.gap = n.itemSpacing;
    if (n.layoutMode === 'GRID') {
      if (n.gridColumns) out.columns = tracksToSpec(n.gridColumns);
      if (n.gridRows) out.rows = tracksToSpec(n.gridRows);
      if (n.counterAxisSpacing !== undefined) out.rowGap = n.counterAxisSpacing;
    }
    const pad = [n.paddingTop ?? 0, n.paddingRight ?? 0, n.paddingBottom ?? 0, n.paddingLeft ?? 0];
    if (pad.some(Boolean)) out.padding = pad.every((v) => v === pad[0]) ? pad[0] : pad;
    if (n.primaryAxisAlign && n.primaryAxisAlign !== 'MIN') out.align = n.primaryAxisAlign.toLowerCase().replace('_', '-');
    if (n.counterAxisAlign && n.counterAxisAlign !== 'MIN') out.crossAlign = n.counterAxisAlign.toLowerCase();
    if (n.layoutWrap) out.wrap = true;
  }
  if (n.layoutSizingH && n.layoutSizingH !== 'FIXED') out.sizingH = n.layoutSizingH.toLowerCase();
  if (n.layoutSizingV && n.layoutSizingV !== 'FIXED') out.sizingV = n.layoutSizingV.toLowerCase();
  if (n.layoutPositioning === 'ABSOLUTE') out.absolute = true;
  if (n.gridColumn !== undefined) out.gridColumn = n.gridColumn;
  if (n.gridRow !== undefined) out.gridRow = n.gridRow;
  if (n.gridColumnSpan) out.colSpan = n.gridColumnSpan;
  if (n.gridRowSpan) out.rowSpan = n.gridRowSpan;
  if (n.characters !== undefined && n.characters !== '') out.text = n.characters;
  if (n.textRuns?.length && n.characters) {
    // read back as spans so the agent can round-trip mixed styles
    const spans: Record<string, unknown>[] = [];
    let pos = 0;
    const chars = n.characters;
    for (const r of [...n.textRuns].sort((a, b) => a.start - b.start)) {
      if (r.start > pos) spans.push({ text: chars.slice(pos, r.start) });
      const sp: Record<string, unknown> = { text: chars.slice(r.start, r.end) };
      if (r.fontWeight) sp.fontWeight = r.fontWeight;
      if (r.italic) sp.italic = true;
      if (r.fontSize) sp.fontSize = r.fontSize;
      if (r.fontFamily) sp.font = r.fontFamily;
      if (r.color) sp.color = colorToHex(r.color);
      if (r.textDecoration === 'UNDERLINE') sp.underline = true;
      if (r.textDecoration === 'STRIKETHROUGH') sp.strike = true;
      if (r.link) sp.link = r.link;
      spans.push(sp);
      pos = r.end;
    }
    if (pos < chars.length) spans.push({ text: chars.slice(pos) });
    out.text = spans;
  }
  if (n.listStyle) out.list = n.listStyle.toLowerCase();
  if (n.textCase && n.textCase !== 'ORIGINAL') out.textCase = n.textCase.toLowerCase();
  if (n.textTruncation === 'ENDING') out.truncate = n.maxLines ?? true;
  if (n.textDecoration && n.textDecoration !== 'NONE') out.decoration = n.textDecoration.toLowerCase();
  if (n.textPath) out.onPath = n.textPath;
  if (n.overflowScroll) out.scroll = n.overflowScroll.toLowerCase();
  if (n.scrollFixed) out.fixed = true;
  if (n.devStatus) out.devStatus = n.devStatus.toLowerCase();
  if (n.widget) out.widget = { name: n.widget.name, state: n.widget.state };
  if (n.annotations?.length) out.annotations = n.annotations;
  if (n.openTypeFeatures && Object.keys(n.openTypeFeatures).length) out.features = Object.fromEntries(Object.entries(n.openTypeFeatures).map(([k, v]) => [k.toLowerCase(), v]));
  if (n.textPathOffset) out.pathOffset = n.textPathOffset;
  if (n.paragraphs?.length) out.paragraphs = n.paragraphs.map((f) => ({ ...(f.list ? { list: f.list.toLowerCase() } : {}), ...(f.level ? { level: f.level } : {}) }));
  if (n.characters !== undefined) {
    if (n.fontFamily !== def.fontFamily) out.font = n.fontFamily;
    if (n.fontSize !== def.fontSize) out.fontSize = n.fontSize;
    if (n.fontWeight !== def.fontWeight) out.fontWeight = n.fontWeight;
    if (n.textAlign && n.textAlign !== def.textAlign) out.textAlign = n.textAlign.toLowerCase();
    if (n.textColor && n.type !== 'TEXT') out.color = colorToHex(n.textColor);
  }
  if (n.shape) out.shape = n.shape.toLowerCase();
  if (n.type === 'CONNECTOR') {
    const e = (c: NodeData['connectorStart']) => (c?.nodeId ? c.nodeId : { x: r2(c?.x ?? 0), y: r2(c?.y ?? 0) });
    out.from = e(n.connectorStart);
    out.to = e(n.connectorEnd);
    out.line = n.connectorType?.toLowerCase();
  }
  if (n.componentId) out.component = n.componentId;
  const sname = (id?: NodeId) => (id ? (doc.get(id)?.name ?? id) : undefined);
  if (n.fillStyleId) out.fillStyle = sname(n.fillStyleId);
  if (n.gridStyleId) out.gridStyle = sname(n.gridStyleId);
  if (n.strokeStyleId) out.strokeStyle = sname(n.strokeStyleId);
  if (n.textStyleId) out.textStyle = sname(n.textStyleId);
  if (n.effectStyleId) out.effectStyle = sname(n.effectStyleId);
  if (n.boundVariables) out.bind = Object.fromEntries(Object.entries(n.boundVariables).map(([k, v]) => [k, sname(v)]));
  if (n.variableModes)
    out.modes = Object.fromEntries(
      Object.entries(n.variableModes).map(([c, m]) => {
        const col = doc.get(c);
        return [col?.name ?? c, col?.modes?.find((x) => x.id === m)?.name ?? m];
      }),
    );
  if (n.path) out.path = n.path.length > 400 ? n.path.slice(0, 400) + '…' : n.path;
  if (n.pageKind) out.pageKind = n.pageKind;
  const kids = doc.children(id);
  if (kids.length) {
    if (depth > 0) out.children = kids.map((k) => nodeToSpec(doc, k.id, depth - 1));
    else out.childCount = kids.length;
  }
  return out;
}

/** One line per node outline — the cheapest way for an agent to understand a page. */
export function describeTree(doc: Document, id: NodeId, maxDepth = 6, maxNodes = 400): string {
  const lines: string[] = [];
  let count = 0;
  doc.walk(id, (n, depth) => {
    if (count >= maxNodes) return false;
    count++;
    const pad = '  '.repeat(depth);
    let s = `${pad}${n.type} "${n.name}" #${n.id}`;
    if (n.type !== 'PAGE' && n.type !== 'DOCUMENT') s += ` (${r2(n.x)},${r2(n.y)} ${r2(n.width)}×${r2(n.height)})`;
    if (n.rotation) s += ` rot=${r2(n.rotation)}`;
    if (n.layoutMode && n.layoutMode !== 'NONE') s += ` ${n.layoutMode === 'HORIZONTAL' ? 'row' : n.layoutMode === 'GRID' ? `grid ${n.gridColumns?.length ?? 2}cols` : 'col'} gap=${n.itemSpacing ?? 0}`;
    if (n.characters) s += ` "${n.characters.length > 60 ? n.characters.slice(0, 57) + '...' : n.characters}"`.replace(/\n/g, '\\n');
    const f = n.fills.find((p) => p.type === 'SOLID' && p.visible !== false);
    if (f && f.type === 'SOLID') s += ` fill=${colorToHex(f.color)}`;
    if (!n.visible) s += ' hidden';
    if (n.locked) s += ' locked';
    if (n.type === 'CONNECTOR') s += ` ${n.connectorStart?.nodeId ?? 'free'}→${n.connectorEnd?.nodeId ?? 'free'}`;
    lines.push(s);
    if (depth >= maxDepth) {
      const c = doc.children(n.id).length;
      if (c) lines.push(`${pad}  … ${c} children`);
      return false;
    }
  });
  if (count >= maxNodes) lines.push(`… truncated at ${maxNodes} nodes`);
  return lines.join('\n');
}
